import { Builder } from "@builder.io/sdk";
import { type EmblaPluginType as CarouselPlugin } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import { BlogTypography } from "~/components/ui/blog-typography";
import {
  CarouselContent,
  Carousel as BaseCarousel,
  CarouselItem,
  CarouselDots,
} from "~/components/ui/carousel";
import { cn, imageLoader } from "~/utils";

interface Props {
  headline: string;
  items: Array<{
    name: string;
    role: string;
    content: string;
    avatar?: string;
  }>;
  autoplay: boolean;
  pauseOnHover: boolean;
  delay: number;
}

const TestimonialCarousel = ({
  items = [],
  headline,
  autoplay = true,
  pauseOnHover = false,
  delay = 4,
}: Props) => {
  return (
    <section
      className={cn(
        "relative flex h-auto items-center justify-center overflow-hidden bg-brand-primary-1 md:h-auto md:max-h-[560px]",
        {
          "md:max-h-[640px]": !!headline,
        }
      )}
    >
      <BaseCarousel
        plugins={
          [
            autoplay &&
              Autoplay({ delay: delay * 1000, stopOnMouseEnter: pauseOnHover }),
          ].filter(Boolean) as CarouselPlugin[]
        }
        className={cn(
          "z-10 flex h-full w-auto max-w-fit flex-col gap-2 overflow-hidden pb-20",
          {
            "pt-20": !!headline,
          }
        )}
        opts={{
          active: items.length > 1,
        }}
      >
        {headline ? (
          <BlogTypography
            variant="h4"
            color="white"
            align="center"
            className="mb-8"
          >
            {headline}
          </BlogTypography>
        ) : null}
        <CarouselContent
          className={cn("h-full", {
            "pt-20": !headline,
          })}
        >
          {items.map((item, index) => {
            return (
              <CarouselItem
                key={index}
                className="flex w-screen flex-col place-items-center items-center justify-center gap-8 px-6"
              >
                <BlogTypography
                  variant="h3"
                  fontFamily="serif"
                  color="white"
                  className="max-w-[50ch] text-[1.375rem] md:text-[1.75rem]"
                  align="center"
                >
                  {item.content}
                </BlogTypography>
                <div className="flex max-w-[50ch] flex-col items-center gap-4">
                  {!!item.avatar && (
                    <Image
                      src={item.avatar}
                      alt={item.name}
                      width={112.5}
                      height={112.5}
                      className="rounded-full"
                      loader={imageLoader}
                    />
                  )}
                  <BlogTypography
                    variant="subtitle-small"
                    color="white"
                    align="center"
                    className="mt-4"
                  >
                    {item.name}
                  </BlogTypography>
                  <BlogTypography
                    variant="p3-small"
                    color="white"
                    align="center"
                  >
                    {item.role}
                  </BlogTypography>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        {items.length > 1 && <CarouselDots className="self-center pt-4" />}
      </BaseCarousel>
      <div className="absolute -bottom-3 left-1/2 flex -translate-x-1/2 scale-[2.5] items-center justify-center rounded-full text-brand-primary-2/50 sm:-bottom-4 sm:scale-[4] md:bottom-0 md:scale-[5] lg:scale-[6.5] xl:scale-[7.5] 2xl:scale-[8.5]">
        {bpdLogo}
      </div>
    </section>
  );
};

const registerTestimonialCarousel = () => {
  Builder.registerComponent(TestimonialCarousel, {
    name: "Testimonial Carousel",
    inputs: [
      {
        name: "autoplay",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "pauseOnHover",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "delay",
        type: "number",
        defaultValue: 4,
      },
      {
        name: "headline",
        type: "string",
        defaultValue: "What people are saying",
      },
      {
        name: "items",
        type: "list",
        subFields: [
          {
            name: "name",
            type: "string",
          },
          {
            name: "role",
            type: "string",
          },
          {
            name: "content",
            type: "string",
          },
          {
            name: "avatar",
            type: "file",
            allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
          },
        ],
      },
    ],
  });
};

export { TestimonialCarousel, registerTestimonialCarousel };

const bpdLogo = (
  <svg
    width="162"
    height="68"
    viewBox="0 0 162 68"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="BPD logo">
      <path
        id="Vector"
        d="M161.792 34.2786V33.9985V0.890625H150.794V13.1403C146.421 9.83516 141.285 8.06119 135.461 7.87446C127.943 7.57569 121.337 10.1153 115.847 15.4372C111.549 19.62 108.925 24.4751 108.013 29.9464C107.157 24.6992 104.701 20.0308 100.719 15.9974C95.6386 10.8622 89.3675 8.15456 82.0916 7.91181C81.7753 7.89313 81.4589 7.89313 81.1426 7.89313C73.9783 7.89313 67.67 10.4701 62.348 15.5305C58.0308 19.6387 55.4256 24.3817 54.551 29.7036C53.602 24.3444 50.9968 19.5453 46.7541 15.4372C41.2832 10.1153 34.6585 7.57569 27.1407 7.87446C21.3162 8.06119 16.1803 9.81649 11.8073 13.1403V0.890625H0.809674V33.9985V34.2786C0.623589 41.5425 3.35904 47.9288 8.92298 53.232C14.245 58.3112 20.5719 60.8694 27.7176 60.8694C28.0339 60.8694 28.3503 60.8694 28.6666 60.8508C35.9611 60.608 42.2136 57.9004 47.2937 52.7652C50.8107 49.2173 53.1182 45.1465 54.2161 40.6462V67.8906H65.2137V55.6409C69.5867 58.9461 74.7226 60.7201 80.5471 60.9068C88.0649 61.2056 94.671 58.666 100.16 53.3441C104.459 49.1613 107.083 44.3062 107.995 38.8349C108.851 44.0821 111.307 48.7504 115.289 52.7839C120.369 57.9191 126.64 60.6267 133.916 60.8694C134.233 60.8881 134.549 60.8881 134.865 60.8881C142.03 60.8881 148.338 58.3112 153.66 53.2507C159.261 47.9288 161.978 41.5425 161.792 34.2786ZM39.4595 45.3519C36.4077 48.5637 32.7977 50.2256 28.4061 50.431C23.7912 50.6178 19.9206 49.0866 16.5711 45.7627C13.2029 42.4015 11.6398 38.5361 11.7887 33.9425C11.8817 29.5916 13.4635 25.9689 16.5711 22.8692C19.6601 19.8067 23.2515 18.3129 27.5687 18.3129C32.1092 18.3129 35.8681 19.8254 39.0501 22.9065C42.2508 26.025 43.8511 29.6476 43.9442 33.9798C44 38.4054 42.5299 42.1214 39.4595 45.3519ZM92.4752 45.8561C89.3117 48.9558 85.5528 50.4497 80.9937 50.4497C76.6765 50.4497 73.0851 48.9558 69.9961 45.8934C66.8885 42.7936 65.3067 39.1897 65.2137 34.8201C65.0648 30.2078 66.6279 26.3611 69.9961 22.9999C73.3456 19.676 77.2162 18.1448 81.8311 18.3315C86.2041 18.5183 89.8327 20.1802 92.8845 23.4107C95.9549 26.6599 97.425 30.3572 97.3692 34.7641C97.2762 39.115 95.6758 42.7376 92.4752 45.8561ZM146.012 45.7627C142.662 49.0866 138.792 50.6178 134.177 50.431C129.804 50.2443 126.175 48.5824 123.123 45.3519C120.053 42.1027 118.583 38.4054 118.639 33.9985C118.75 29.6663 120.351 26.025 123.533 22.9252C126.696 19.8254 130.455 18.3315 135.014 18.3315C139.331 18.3315 142.923 19.8254 146.012 22.8878C149.119 25.9876 150.701 29.5916 150.794 33.9611C150.962 38.5361 149.38 42.4015 146.012 45.7627Z"
        fill="currentColor"
      />
    </g>
  </svg>
);
